import React from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'

interface LinkComponentProps {
  children: React.ReactNode
  skipLocaleHandling?: boolean
  href?: string
  locale?: string
  [key: string]: any
}

const LinkComponent = ({ children, skipLocaleHandling, ...rest }: LinkComponentProps) => {
  const router = useRouter()
  
  // Extract locale from router - prioritize asPath (most reliable), then query, then pathname
  let currentLocale = rest.locale || ''
  
  // First, try to extract from asPath (actual URL path like /ja/product/...)
  // This is the most reliable method as it's always available
  if (!currentLocale && router.asPath) {
    const asPathStr = router.asPath.split('?')[0] // Remove query string if present
    const asPathMatch = asPathStr.match(/^\/([a-z]{2})(\/|$)/)
    if (asPathMatch) {
      currentLocale = asPathMatch[1]
    }
  }
  
  // Fallback to query (works with [locale] folder structure)
  // router.query.locale is available when using pages/[locale]/...
  if (!currentLocale && router.query?.locale) {
    const queryLocale = router.query.locale
    currentLocale = Array.isArray(queryLocale) ? queryLocale[0] : (queryLocale as string)
  }
  
  // Last resort: check pathname
  if (!currentLocale && router.pathname) {
    const pathnameMatch = router.pathname.match(/^\/([a-z]{2})(\/|$)/)
    if (pathnameMatch) {
      currentLocale = pathnameMatch[1]
    }
  }

  let href = rest.href
  
  // If no href provided, use asPath but remove locale prefix if present
  if (!href) {
    href = router.asPath
    // Remove locale from asPath if we're using it as href
    if (currentLocale && href && typeof href === 'string') {
      const hrefStr = href.split('?')[0] // Remove query string
      if (hrefStr.startsWith(`/${currentLocale}/`) || hrefStr === `/${currentLocale}`) {
        href = hrefStr.replace(`/${currentLocale}`, '') || '/'
      }
    }
  }
  
  // Handle external links
  if (href && typeof href === 'string' && (href.indexOf('http') === 0 || href.indexOf('//') === 0)) {
    skipLocaleHandling = true
  }
  
  // Add locale prefix if needed
  if (currentLocale && !skipLocaleHandling && href && typeof href === 'string') {
    const hrefStr = href
    
    // Check if href already starts with a locale prefix
    const localePrefixes = ['en', 'ja', 'es', 'ko']
    const hasLocalePrefix = localePrefixes.some(locale => hrefStr.startsWith(`/${locale}/`) || hrefStr === `/${locale}`)
    
    // Only add locale if:
    // 1. href doesn't already have a locale prefix
    // 2. href is not an anchor link (starts with #)
    // 3. href is not empty or just "/"
    if (!hasLocalePrefix && !hrefStr.startsWith('#') && hrefStr !== '/' && hrefStr !== '') {
      // Ensure href starts with /
      const normalizedHref = hrefStr.startsWith('/') ? hrefStr : `/${hrefStr}`
      href = `/${currentLocale}${normalizedHref}`
    } else {
      href = hrefStr
    }
  }

  return (
    <Link href={href} {...rest}>
      {children}
    </Link>
  )
}

export default LinkComponent